import { Injectable } from '@angular/core';
import { PlaybookViewTimeEvent } from '../models/playbook';
import { LocalStorageService } from './local-storage.service';
import { PlaybookService } from './playbook.service';

interface QueuedPlaybookViewTime { event: PlaybookViewTimeEvent; queuedAt: string; }

@Injectable({providedIn: 'root'})
export class PlaybookViewTimeQueueService {
  private readonly maxSize = 2000;
  private readonly maxAgeMs = 7 * 24 * 60 * 60 * 1000;
  private readonly batchSize = 100;
  private flushing = new Set<string>();
  private batchTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private retryTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private retryAttempts = new Map<string, number>();

  constructor(private storage: LocalStorageService, private playbookService: PlaybookService) {
    window.addEventListener('online', () => this.flushCurrentUser());
    this.storage.listenForUserUpdate().subscribe(user => user?._id && this.flush(user._id));
  }

  createEventId(): string {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const value = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
  }

  enqueue(userid: string, event: PlaybookViewTimeEvent, flushImmediately = false) {
    if (!userid || !event?.eventid) return;
    const queue = this.read(userid);
    if (queue.some(item => item.event.eventid === event.eventid)) return;
    queue.push({event, queuedAt: new Date().toISOString()});
    if (queue.length > this.maxSize) {
      const removed = queue.splice(0, queue.length - this.maxSize);
      console.log('Playbook view-time queue reached its size limit', {userid, removed: removed.length});
    }
    this.write(userid, queue);
    if (flushImmediately || queue.length >= this.batchSize) this.flush(userid);
    else this.scheduleBatch(userid);
  }

  flushCurrentUser() { const userid = this.storage.getUser()?._id; if (userid) this.flush(userid); }

  async flush(userid: string): Promise<void> {
    if (!userid || !navigator.onLine || this.storage.getUser()?._id !== userid || this.flushing.has(userid)) return;
    this.batchTimers.get(userid) && clearTimeout(this.batchTimers.get(userid));
    this.batchTimers.delete(userid);
    const batch = this.read(userid).slice(0, this.batchSize);
    if (!batch.length) return;
    this.flushing.add(userid);
    try {
      const response = await this.playbookService.postPlaybookViewTimeEvents(batch.map(item => item.event));
      if (!response.success) throw new Error('Playbook view-time request failed');
      const accepted = response.result?.acceptedEventids || [];
      const rejected = (response.result?.rejected || []).map(item => item.eventid).filter(Boolean);
      if (response.result?.rejected?.length) console.log('Playbook view-time events rejected', response.result.rejected);
      const completed = new Set([...accepted, ...rejected]);
      if (!completed.size) throw new Error('No playbook view-time events were acknowledged');
      const remaining = this.read(userid).filter(item => !completed.has(item.event.eventid));
      this.write(userid, remaining);
      this.retryAttempts.delete(userid);
      const retry = this.retryTimers.get(userid); if (retry) clearTimeout(retry);
      this.retryTimers.delete(userid);
      if (remaining.length >= this.batchSize) setTimeout(() => this.flush(userid), 1000);
      else if (remaining.length) this.scheduleBatch(userid);
    } catch (err) {
      console.log('Unable to submit queued playbook view time', err);
      this.scheduleRetry(userid);
    } finally { this.flushing.delete(userid); }
  }

  private read(userid: string): QueuedPlaybookViewTime[] {
    const queue = this.storage.getPlaybookViewTimeQueue<QueuedPlaybookViewTime>(userid);
    const cutoff = Date.now() - this.maxAgeMs;
    const valid = queue.filter(item => item?.event?.eventid && new Date(item.queuedAt).getTime() >= cutoff);
    if (valid.length !== queue.length) {
      console.log('Expired queued playbook view time removed', {userid, removed: queue.length - valid.length});
      this.write(userid, valid);
    }
    return valid;
  }
  private write(userid: string, queue: QueuedPlaybookViewTime[]) { this.storage.setPlaybookViewTimeQueue(userid, queue); }
  private scheduleBatch(userid: string) {
    if (this.batchTimers.has(userid)) return;
    this.batchTimers.set(userid, setTimeout(() => { this.batchTimers.delete(userid); this.flush(userid); }, 30000));
  }
  private scheduleRetry(userid: string) {
    if (this.retryTimers.has(userid)) return;
    const attempt = (this.retryAttempts.get(userid) || 0) + 1;
    this.retryAttempts.set(userid, attempt);
    const delay = Math.min(30000 * Math.pow(2, attempt - 1), 15 * 60 * 1000);
    this.retryTimers.set(userid, setTimeout(() => { this.retryTimers.delete(userid); this.flush(userid); }, delay));
  }
}
